import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ErreurApi, appelApi, useRequete } from '../api/client';
import type { Equipe, Fonction, Personne } from '../api/types';
import { Badge, Chargement, Message } from '../composants/elements';

/**
 * La page d'un groupe — §5 de la spec lieux-et-hiérarchie. Remplace l'édition
 * en ligne du tableau des groupes et le modal de création : `id === 'new'`
 * rend un formulaire de création réduit (Name / Purpose / Nature) ; tout
 * autre id rend l'identité éditable, les lieux, les membres, et les forces /
 * trous déjà calculés par `ficheEquipe` pour l'écran de suivi — les brancher
 * ici coûte un appel, pas une lecture nouvelle.
 */

type ReponseEquipes = {
  equipes: Equipe[];
  natures: { tag: string; libelle: string; definition: string | null }[];
  raisonEtreMax: number;
};

type PersonneAvecAppartenances = Personne & {
  appartenances: { equipeId: string; fonction: string | null; libelleFonction: string }[];
};

type ReponsePersonnes = {
  personnes: PersonneAvecAppartenances[];
  fonctions: Record<string, Fonction[]>;
};

type FicheEquipe = {
  equipe: string;
  effectif: number;
  forces: { titre: string; anneau: string; couverture: number; experts: number }[];
  trous: { titre: string; anneau: string; couverture: number }[];
};

export function EquipeDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const estCreation = id === 'new';

  const equipes = useRequete<ReponseEquipes>('/api/admin/equipes');
  // En création, il n'y a ni membres ni forces à afficher — pas d'appel.
  const personnes = useRequete<ReponsePersonnes>(estCreation ? null : '/api/admin/personnes');
  const fiche = useRequete<FicheEquipe>(!estCreation && id ? `/api/dashboards/equipe/${id}` : null, [id]);

  const [avis, setAvis] = useState<{ variante: 'succes' | 'erreur'; texte: string } | null>(null);
  const [emailAAjouter, setEmailAAjouter] = useState('');
  const [fonctionAAjouter, setFonctionAAjouter] = useState('');

  // --- Formulaire de création --------------------------------------------
  const [nomCreation, setNomCreation] = useState('');
  const [raisonEtreCreation, setRaisonEtreCreation] = useState('');
  const [natureCreation, setNatureCreation] = useState('');
  const [creationEnCours, setCreationEnCours] = useState(false);

  if (equipes.erreur) {
    return (
      <div className="contenu section">
        <Message variante="erreur">{equipes.erreur.message}</Message>
      </div>
    );
  }
  if (!equipes.donnees) return <Chargement />;

  const raisonEtreMax = equipes.donnees.raisonEtreMax;
  const natures = equipes.donnees.natures;

  if (estCreation) {
    const raisonPropre = raisonEtreCreation.trim();
    const peutCreer = nomCreation.trim().length >= 2 && raisonPropre.length > 0 && natureCreation !== '';

    async function cree(): Promise<void> {
      if (!peutCreer || creationEnCours) return;
      setCreationEnCours(true);
      try {
        const r = await appelApi<{ equipe: Equipe }>('/api/admin/equipes', {
          methode: 'POST',
          corps: { nom: nomCreation.trim(), raisonEtre: raisonPropre, tags: [natureCreation] },
        });
        navigate(`/administration/equipes/${r.equipe.id}`, { replace: true });
      } catch (cause) {
        setAvis({
          variante: 'erreur',
          texte: cause instanceof ErreurApi ? cause.message : 'Creation failed',
        });
      } finally {
        setCreationEnCours(false);
      }
    }

    return (
      <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
        <div className="entete-page">
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Admin · New team
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Create a team</h1>
        </div>

        {avis ? (
          <div style={{ marginTop: 20 }}>
            <Message variante={avis.variante}>{avis.texte}</Message>
          </div>
        ) : null}

        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 32, maxWidth: 520 }}
          onSubmit={(e) => {
            e.preventDefault();
            void cree();
          }}
        >
          <label className="champ">
            <span className="champ__libelle">Name</span>
            <input
              className="saisie"
              placeholder="Data engineering"
              value={nomCreation}
              onChange={(e) => setNomCreation(e.target.value)}
              aria-label="Name"
              required
            />
          </label>

          <label className="champ">
            <span className="champ__libelle">Nature</span>
            <select
              className="selection"
              value={natureCreation}
              onChange={(e) => setNatureCreation(e.target.value)}
              aria-label="Nature"
              required
            >
              <option value="">Select…</option>
              {natures.map((n) => (
                <option key={n.tag} value={n.tag}>
                  {n.libelle}
                </option>
              ))}
            </select>
          </label>

          <label className="champ">
            <span className="champ__libelle">Purpose</span>
            <textarea
              className="zone-texte"
              rows={3}
              value={raisonEtreCreation}
              onChange={(e) => setRaisonEtreCreation(e.target.value.slice(0, raisonEtreMax))}
              placeholder="What this team is for — a name alone does not say it."
              aria-label="Purpose"
              required
            />
            <span className="champ__aide">
              {raisonEtreCreation.length} / {raisonEtreMax}
            </span>
          </label>

          <div className="barre-actions">
            <button type="submit" className="bouton bouton--primaire" disabled={!peutCreer || creationEnCours}>
              Create
            </button>
          </div>
        </form>

        {/* Le scope géographique, les membres et l'agrégation des compétences
            se configurent une fois le groupe créé : créer, puis configurer,
            plutôt que tout dans un seul formulaire (§5). */}
      </div>
    );
  }

  // --- Mode édition --------------------------------------------------------

  if (!personnes.donnees) return <Chargement />;

  const equipe = equipes.donnees.equipes.find((e) => e.id === id);
  if (!equipe) {
    return (
      <div className="contenu section">
        <Message variante="erreur">Team not found.</Message>
      </div>
    );
  }

  // Les lieux archivés ne sont pas proposés : scoper un groupe sur un lieu
  // qu'on vient de fermer est un geste qu'on ne veut pas offrir. Un scope déjà
  // posé sur un lieu depuis archivé n'en est pas retiré pour autant.
  const lieuxDisponibles = equipes.donnees.equipes.filter(
    (e) => e.nature === 'lieu' && e.actif && e.id !== equipe.id,
  );
  const membres = personnes.donnees.personnes.filter((p) =>
    p.appartenances.some((a) => a.equipeId === equipe.id),
  );
  const fonctionsProposees = personnes.donnees.fonctions[equipe.nature ?? ''] ?? [];

  async function agit(action: () => Promise<string>): Promise<void> {
    try {
      const texte = await action();
      equipes.recharge();
      personnes.recharge();
      setAvis({ variante: 'succes', texte });
    } catch (cause) {
      setAvis({
        variante: 'erreur',
        texte: cause instanceof ErreurApi ? cause.message : 'Action failed',
      });
    }
  }

  async function modifie(corps: Record<string, unknown>): Promise<void> {
    await agit(async () => {
      await appelApi(`/api/admin/equipes/${equipe!.id}`, { methode: 'PATCH', corps });
      return 'Team updated.';
    });
  }

  async function definitLieux(lieuxIds: string[]): Promise<void> {
    await modifie({ lieuxIds });
  }

  async function ajouteMembre(): Promise<void> {
    const trouvee = personnes.donnees!.personnes.find(
      (p) => p.email.toLowerCase() === emailAAjouter.trim().toLowerCase(),
    );
    if (!trouvee) {
      setAvis({ variante: 'erreur', texte: `No person found for “${emailAAjouter}”.` });
      return;
    }
    const autres = trouvee.appartenances.filter((a) => a.equipeId !== equipe!.id);
    await agit(async () => {
      await appelApi(`/api/admin/personnes/${trouvee.id}`, {
        methode: 'PATCH',
        corps: {
          appartenances: [
            ...autres.map((a) => ({ equipeId: a.equipeId, fonction: a.fonction })),
            { equipeId: equipe!.id, fonction: fonctionAAjouter || null },
          ],
        },
      });
      setEmailAAjouter('');
      setFonctionAAjouter('');
      return `${trouvee.nom} added.`;
    });
  }

  async function retireMembre(personne: PersonneAvecAppartenances): Promise<void> {
    const autres = personne.appartenances.filter((a) => a.equipeId !== equipe!.id);
    await agit(async () => {
      await appelApi(`/api/admin/personnes/${personne.id}`, {
        methode: 'PATCH',
        corps: { appartenances: autres.map((a) => ({ equipeId: a.equipeId, fonction: a.fonction })) },
      });
      return `${personne.nom} removed.`;
    });
  }

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
          <p className="eyebrow" style={{ marginBottom: -6 }}>
            Admin · {equipe.libelleNature ?? 'Team'}
          </p>
          <label className="champ">
            <span className="champ__libelle">Name</span>
            <input
              className="saisie"
              defaultValue={equipe.nom}
              aria-label="Name"
              onBlur={(e) => {
                const valeur = e.target.value.trim();
                if (valeur.length >= 2 && valeur !== equipe.nom) void modifie({ nom: valeur });
              }}
            />
          </label>
          <label className="champ">
            <span className="champ__libelle">Nature</span>
            <select
              className="selection"
              defaultValue={equipe.nature ?? ''}
              aria-label="Nature"
              onChange={(e) => {
                // « Not set » n'est pas une nature : l'envoyer produirait
                // `{ tags: [''] }`, que `normaliseTags` réduit à une liste vide
                // — une dénaturation involontaire plutôt qu'un non-choix.
                if (e.target.value === '') return;
                void modifie({ tags: [e.target.value] });
              }}
            >
              {equipe.nature === null ? <option value="">Not set</option> : null}
              {natures.map((n) => (
                <option key={n.tag} value={n.tag}>
                  {n.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="champ">
            <span className="champ__libelle">Purpose</span>
            <textarea
              className="zone-texte"
              rows={3}
              defaultValue={equipe.raisonEtre ?? ''}
              maxLength={raisonEtreMax}
              aria-label="Purpose"
              onBlur={(e) => {
                const valeur = e.target.value.trim();
                if (valeur !== (equipe.raisonEtre ?? '')) void modifie({ raisonEtre: valeur || null });
              }}
            />
          </label>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {equipe.actif ? <Badge variante="ok">active</Badge> : <Badge>archived</Badge>}
          <button
            type="button"
            className="bouton bouton--fantome bouton--petit"
            onClick={() => void modifie({ actif: !equipe.actif })}
          >
            {equipe.actif ? 'Archive' : 'Restore'}
          </button>
        </div>
      </div>

      {avis ? (
        <div style={{ marginTop: 20 }}>
          <Message variante={avis.variante}>{avis.texte}</Message>
        </div>
      ) : null}

      {/* Lieux — absent pour un groupe qui est lui-même un lieu (§3.2). */}
      {equipe.nature !== 'lieu' ? (
        <section style={{ marginTop: 32 }}>
          <h3>Locations</h3>
          <select
            multiple
            className="selection"
            aria-label="Locations"
            value={equipe.lieux.map((l) => l.id)}
            onChange={(e) => {
              const choisis = [...e.target.selectedOptions].map((o) => o.value);
              void definitLieux(choisis);
            }}
          >
            {lieuxDisponibles.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nom}
              </option>
            ))}
          </select>
        </section>
      ) : null}

      <section style={{ marginTop: 32 }}>
        <h3>Members ({membres.length})</h3>
        <ul>
          {membres.map((p) => {
            const appartenance = p.appartenances.find((a) => a.equipeId === equipe.id)!;
            return (
              <li key={p.id}>
                <span>{p.nom}</span> — <span className="discret">{appartenance.libelleFonction}</span>{' '}
                <button type="button" className="bouton bouton--fantome bouton--petit" onClick={() => void retireMembre(p)}>
                  Remove
                </button>
              </li>
            );
          })}
        </ul>

        <div className="barre-actions" style={{ marginTop: 12 }}>
          <input
            className="saisie"
            placeholder="person@elyadata.com"
            value={emailAAjouter}
            onChange={(e) => setEmailAAjouter(e.target.value)}
            aria-label="Email to add"
          />
          <select
            className="selection"
            value={fonctionAAjouter}
            onChange={(e) => setFonctionAAjouter(e.target.value)}
            aria-label="Function"
          >
            <option value="">Member</option>
            {fonctionsProposees.map((f) => (
              <option key={f.cle} value={f.cle}>
                {f.libelle}
              </option>
            ))}
          </select>
          <button type="button" className="bouton bouton--primaire" onClick={() => void ajouteMembre()}>
            Add
          </button>
        </div>
      </section>

      <section style={{ marginTop: 32 }}>
        <h3>Strengths and gaps</h3>
        {!fiche.donnees ? (
          <Chargement />
        ) : (
          <>
            <ul>
              {fiche.donnees.forces.map((f) => (
                <li key={f.titre}>
                  <span>{f.titre}</span>{' '}
                  <span className="discret">
                    — {f.couverture} at level 3+, {f.experts} at level 4+
                  </span>
                </li>
              ))}
            </ul>
            {fiche.donnees.trous.length > 0 ? (
              <>
                <h4>Gaps</h4>
                <ul>
                  {fiche.donnees.trous.map((t) => (
                    <li key={t.titre}>{t.titre}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
